import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { OrganizationUser, Role, RoleResponse, UsersResponse } from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  USERS: {
    LIST: "/api/v2/org/:organization_id/users",
    LIST_ROLES: "/api/v2/org/:organization_id/user-roles",
    UPDATE: "/api/v2/org/:organization_id/users/:user_id",
    DELETE: "/api/v2/org/:organization_id/users/:user_id",
  },
};

export class UsersAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  /**
   * Get all users in the organization (non-admin view)
   */
  async list(config?: AxiosRequestConfig): Promise<OrganizationUser[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<UsersResponse>(
      route(ENDPOINTS.USERS.LIST, { organization_id: orgId || "" }),
      config,
    );
    return response.data.data.users;
  }

  /**
   * Get all organization roles
   */
  async listRoles(config?: AxiosRequestConfig): Promise<Role[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<RoleResponse>(
      route(ENDPOINTS.USERS.LIST_ROLES, {
        organization_id: orgId || "",
      }),
      config,
    );
    return response.data.data.roles.map((r) => {
      return {
        id: r.id,
        name: r.name === "Organization Admin" ? "Admin" : r.name,
        description: r.description,
      };
    });
  }

  /**
   * Update a user in the organization
   */
  async update(
    userId: string,
    userData: Partial<OrganizationUser>,
    config?: AxiosRequestConfig,
  ): Promise<OrganizationUser> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.put<{ data: OrganizationUser }>(
      route(ENDPOINTS.USERS.UPDATE, {
        organization_id: orgId || "",
        user_id: userId,
      }),
      userData,
      config,
    );
    return response.data.data;
  }

  /**
   * Delete a user from the organization
   */
  async delete(userId: string, config?: AxiosRequestConfig): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.USERS.DELETE, {
        organization_id: orgId || "",
        user_id: userId,
      }),
      config,
    );
  }
}
